/**
 * Capital Projects, for the chief executive — the screen. The portfolio in
 * numbers, every live project with its verdict, and the finance lines.
 */
import { financeOrigin } from "../../domain/finance";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import {
  answerCapex,
  buildCapexDesk,
  CAPEX_MODEL_PROMPT,
  materialForCapex,
  VERDICT_LABEL,
  type CapexDesk,
} from "../../domain/capexDesk";
import { money } from "../../domain/intelligence";
import type { ProjectRow } from "../../domain/projects";
import { Button, Card, ErrorState, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useFinance } from "./FinanceForm";
import { AskCard, Bars, Refs, Tiles, x as s } from "./shared";
import { useScreenContext } from "../../assistant/screen";
import { t } from "../../i18n";

function useCapexRecords() {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["capex-records", siteId],
    staleTime: 60_000,
    queryFn: async () => {
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [projects, holdPoints] = await Promise.all([
        quiet(source.listProjects({ siteId }), [] as ProjectRow[]),
        quiet(source.listHoldPoints(siteId), null),
      ]);
      return { projects, holdPoints };
    },
  });
}

export function CapexPage() {
  const session = useSession();
  const records = useCapexRecords();
  const finance = useFinance();
  const desk = useMemo<CapexDesk | null>(() => {
    if (!records.data) return null;
    return buildCapexDesk({
      now: new Date(),
      siteCode: session.site.code,
      currency: finance.value.currency,
      projects: records.data.projects,
      holdPoints: records.data.holdPoints,
      finance: finance.value,
    });
  }, [records.data, finance.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/capex",
          title: "Capital Projects",
          summary: desk.boardLine,
          material: materialForCapex(desk),
          figures: desk.figures,
          own: (q) => answerCapex(q, desk),
          prompt: CAPEX_MODEL_PROMPT,
          offers: [
            { label: "What is stopped?", ask: "what is stopped at a hold point?" },
            { label: "What is late?", ask: "what is late?" },
            { label: "Where is spend ahead?", ask: "where is spend ahead of the calendar?" },
          ],
        }
      : null,
  );
  return (
    <Page
      /* moved out of Executive with the rest of the money, 2026-09-21 */
      eyebrow={t("{site} · Financial System", { site: session.site.code })}
      title="Capital Projects"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "Capital projects: committed, spent, late, or on hold, with a recommendation for each.",
        )}{" "}
        <Link to="/capital-projects">{t("projects screens")}</Link>.
      </p>
      {records.isPending && <Skeleton rows={8} />}
      {records.isError && <ErrorState error={records.error} retry={() => void records.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          <Card
            title="Every live project"
            sub="Most urgent first"
          >
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>{t("project")}</th>
                    <th>{t("verdict")}</th>
                    <th>{t("budget")}</th>
                    <th>{t("spent")}</th>
                    <th>{t("calendar")}</th>
                    <th>{t("late")}</th>
                    <th>{t("gates")}</th>
                  </tr>
                </thead>
                <tbody>
                  {desk.projects.map((p) => (
                    <tr key={p.id} data-testid={`capex-${p.code}`}>
                      <td>
                        <Link to={`/capital-projects/${p.id}`}>
                          <b>{p.name}</b>
                        </Link>
                        <div className={s.muted}>
                          {p.code} · {p.status}
                        </div>
                      </td>
                      <td>
                        <span
                          className={
                            p.verdict === "hold"
                              ? s.tagRed
                              : p.verdict === "rebaseline"
                                ? s.tag
                                : p.verdict === "watch"
                                  ? s.tagBlue
                                  : s.tagGreen
                          }
                        >
                          {t(VERDICT_LABEL[p.verdict])}
                        </span>
                        <div className={s.muted}>{p.why}</div>
                      </td>
                      <td className={s.num}>{p.budget != null ? money(p.budget, desk.currency) : "—"}</td>
                      <td className={s.num}>
                        {p.spent != null ? money(p.spent, desk.currency) : "—"}
                        {p.spentPct != null && <div className={s.muted}>{p.spentPct}%</div>}
                      </td>
                      <td className={s.num}>{p.elapsedPct != null ? `${p.elapsedPct}%` : "—"}</td>
                      <td className={`${s.num} ${p.slipDays > 30 ? s.neg : ""}`}>
                        {p.slipDays ? `${p.slipDays} d` : "—"}
                      </td>
                      <td>{p.gates}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {desk.financeLines.length > 0 && (
            <Card title="The finance system's capital lines" sub={financeOrigin(finance.value)}>
              <Bars
                rows={desk.financeLines.map((c) => ({
                  label: t("{name} ({pct}% done)", { name: c.name, pct: c.pctComplete }),
                  value: c.spent,
                  against: c.budget,
                }))}
                currency={desk.currency}
              />
              <p className={s.muted}>
                {t("Spend ahead of progress: {list}.", {
                  list: desk.financeLines
                    .map((c) => `${c.name} ${c.burnAhead >= 0 ? "+" : ""}${c.burnAhead} pts`)
                    .join("; "),
                })}
              </p>
              <Refs list={desk.tiles.find((t) => t.key === "finance")?.sources ?? []} />
            </Card>
          )}
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForCapex(desk)}
            figures={desk.figures}
            own={(q) => answerCapex(q, desk)}
            prompt={CAPEX_MODEL_PROMPT}
            placeholder={t("What is stopped? What is late? Where is spend ahead of the calendar?")}
          />
          {desk.gaps.length > 0 && (
            <Card title="What this desk does not cover" sub="so the reader knows what is absent">
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
